<template>
  <div
    class="dropdown vue-dropdown-with-search"
    v-on:copy="doNotCopyElementText"
    v-on:cut="doNotCopyElementText"
  >
    <button
      :class="selectedManufacturerWithAnalyticsClass"
      @click="handleManufactureButtonClick"
      :disabled="isLoading"
      ref="buttonElement"
      v-bind="combinedDataAttributes"
      v-on:copy="doNotCopyElementText"
      v-on:cut="doNotCopyElementText"
    >
      <span :class="dropDownMsgClass">{{ dropDownMsg }}</span>
      <div class="clearbtnAndUpDownIndicatorWrapper">
        <div class="toggleDropdownBtn">
          <img v-if="showUpIcon" class="dropbtn__icon" :src="upIconSrc" />
          <img
            v-else-if="showDownIcon"
            class="dropbtn__icon"
            :src="downIconSrc"
          />
          <div v-else-if="showClearIcon" class="clearbtn">
            <XSvg :fillColor="styleVariable.$black85" width="18" />
          </div>
        </div>

        <LoadingSpinner v-if="isLoading" inlineStyles="margin-right: 12px;" />
      </div>
    </button>
    <div v-if="isLoading" class="skeletonBox loadingDropdown"></div>
    <div v-else @keydown.esc="escapeMenu">
      <div
        id="manufacturerDropdown"
        :class="manufacturerDropdownClass"
        :aria-expanded="manufacturerAriaExpanded"
        ref="manufacturerDropdown"
      >
        <div class="inputWithBtnWrapper">
          <input
            type="text"
            :placeholder="searchPlaceholder"
            class="search-input"
            @input="filterFunction"
            :value="manufacturerFilterText"
            ref="searchManufacturerInput"
            @keyup.enter="manufacturerInputEnter"
            @keyup.down="onInputDownKey"
            :disabled="disableManufacturerInput"
          />
          <div
            tab-index="-1"
            v-if="showClearManufacturerTextIconX"
            class="buttonInsideInput"
            @click="handleClearManufacturerSearchInput"
          >
            <div class="svgWrapper">
              <XSvg :fillColor="styleVariable.$black85" width="12" />
            </div>
          </div>
        </div>

        <div
          class="dropdown-scroll"
          v-on:copy="doNotCopyElementText"
          v-on:cut="doNotCopyElementText"
        >
          <slot name="manufacturers"></slot>
        </div>
      </div>
      <div
        id="modelDropdown"
        :class="modelDropdownClass"
        :aria-expanded="modelAriaExpanded"
        ref="modelDropdown"
      >
        <div class="inputWithBtnWrapper">
          <input
            type="text"
            placeholder="Search compatible models"
            class="search-input model"
            @input="filterModelFunction"
            :value="modelFilterText"
            ref="searchModelInput"
            @keyup.enter="modelInputEnter"
            @keyup.down="onInputDownKey"
          />
          <div
            tab-index="-1"
            v-if="showClearModelTextIconX"
            class="buttonInsideInput"
            @click="handleClearModelSearchInput"
          >
            <div class="svgWrapper">
              <XSvg :fillColor="styleVariable.$black85" width="12" />
            </div>
          </div>
        </div>
        <div
          class="dropdown-scroll"
          v-on:copy="doNotCopyElementText"
          v-on:cut="doNotCopyElementText"
        >
          <slot name="models"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "../../shared/LoadingSpinner.vue";
import XSvg from "../../shared/XSvg.vue";
import javascriptStyleVariables from "../../../styles/javascriptStyleVariables";
import { mapGetters, mapActions, mapState } from "vuex";
const DEFAULT_DROPDOWN_MSG = "Select Option";

export default {
  mounted() {
    this.sendComponentRefs();
  },
  props: {
    // this will handle the case of multiple components on the same page.
    // fired events will also get this id to identify which element if firing
    dropDownId: {
      type: String,
      default() {
        return "";
      }
    },
    dropDownMsg: {
      type: String,
      default() {
        return DEFAULT_DROPDOWN_MSG;
      }
    },
    searchPlaceholder: {
      type: String,
      default() {
        return "Search";
      }
    },
    isLoading: {
      type: Boolean,
      default() {
        return false;
      }
    },
    showManufacturerMenu: {
      type: Boolean,
      default() {
        return false;
      }
    },
    showModelMenu: {
      type: Boolean,
      default() {
        return false;
      }
    },
    manufacturerSelectedByUser: {
      type: Boolean,
      default() {
        return false;
      }
    },
    dataAttributeManufacturer: {
      type: String
    },
    analyticsClassManufacturer: {
      type: String
    },
    dataAttributeSelectedManufacturer: {
      type: String
    },
    analyticsClassSelectedManufacturer: {
      type: String
    }
  },

  methods: {
    ...mapActions("compatibilityChecker", [
      "closeModelsMenu",
      "openManufacturerMenu",
      "closeManufacturerMenu",
      "userSelectManufacturer",
      "manufacturerMenuToggleClicked",
      "updateManufacturerFilterText",
      "updateModelFilterText",
      "updateDisableManufacturerInput",
      "setModelSearchTerm"
    ]),
    toggleMenu() {
      this.manufacturerMenuToggleClicked();
    },
    filterFunction(e) {
      this.$emit(`filterText`, e.target.value);
    },
    filterModelFunction(e) {
      this.$emit(`filterModelText`, e.target.value);
    },
    clearManufacturerText() {
      this.$emit(`filterText`, "");
    },
    clearModelText() {
      this.$emit(`filterModelText`, "");
    },
    async handleClearManufacturerSearchInput(e) {
      e.stopPropagation();
      await this.$nextTick();
      this.clearManufacturerText();
      await this.$nextTick();
      this.$refs.searchManufacturerInput.focus();
    },
    async handleClearModelSearchInput(e) {
      e.stopPropagation();
      await this.$nextTick();
      this.clearModelText();
      await this.$nextTick();
      this.$refs.searchModelInput.focus();
    },
    clearManufacturer() {
      this.clearManufacturerText();
      this.$emit(`clearManufacturer`);
    },
    clearModel() {
      this.clearModelText();
    },
    async handleManufactureButtonClick() {
      const buttonIsHandelingClearSelection = this.manufacturerSelectedByUser;
      if (true === buttonIsHandelingClearSelection) {
        this.clearManufacturer();
        this.clearModel();
        this.closeModelsMenu();
      }
      // toggeling between manufacturer open/closed
      else {
        this.toggleMenu();
        await this.$nextTick();
        if (true === this.showManufacturerMenu) {
          this.$refs.searchManufacturerInput.focus();
        }
      }
    },
    // To send component refs to the parent so they can be used to set focus and for closing during off click
    sendComponentRefs() {
      this.$emit(`send-refs`, {
        refs: this.$refs
      });
    },
    escapeMenu() {
      this.clearManufacturer();
      this.closeManufacturerMenu();
      this.clearModel();
      this.closeModelsMenu();
      this.$refs.buttonElement.focus();
    },
    modelInputEnter(event) {
      const singleModelShown = 1 === this.filteredModelArray.length;
      if (singleModelShown) {
        this.setModelSearchTerm(this.modelFilterText);
        this.onInputEnter(event);
      }
    },
    manufacturerInputEnter(event) {
      const singleManufacturerShown =
        1 === this.filteredManufacturerArray.length;
      if (singleManufacturerShown) {
        this.onInputEnter(event);
      }
    },
    async onInputEnter(event) {
      // finding the closest search option on input enter
      await this.$nextTick();
      const searchedOptionElement = event.target.parentNode.parentNode.querySelector(
        ".dropDown-option"
      );
      searchedOptionElement.focus();
      searchedOptionElement.click();
    },
    onInputDownKey(event) {
      // finding the closest search option on input enter
      this.$nextTick(() => {
        const searchedOptionElement = event.target.parentNode.parentNode.querySelector(
          ".dropDown-option"
        );
        searchedOptionElement.focus();
      });
    },
    doNotCopyElementText(event) {
      event.preventDefault();
    }
  },
  computed: {
    ...mapState("compatibilityChecker", [
      "manufacturerFilterText",
      "modelFilterText",
      "disableManufacturerInput",
      "selectedManufacturerSearchTerm",
      "modelSearchTerm",
      "modelSelectedAnalytics",
      "selectedManufacturer"
    ]),
    ...mapGetters("compatibilityChecker", [
      "isManufacturerMenuLoading",
      "filteredManufacturerArray",
      "filteredModelArray"
    ]),
    manufacturerDropdownClass() {
      const baseClass = "dropdown-content";
      return this.showManufacturerMenu ? `${baseClass} show` : baseClass;
    },
    manufacturerAriaExpanded() {
      return this.showManufacturerMenu;
    },
    modelDropdownClass() {
      const baseClass = "dropdown-content";
      return this.showModelMenu ? `${baseClass} show` : baseClass;
    },
    modelAriaExpanded() {
      return this.showModelMenu;
    },
    dropDownMsgClass() {
      if (this.manufacturerSelectedByUser) {
        return `dropbtn__msg-userSelected`;
      }
      return "dropbtn__msg";
    },
    upIconSrc() {
      return "https://res.cloudinary.com/avd/image/upload/v1633721088/Icons/New%20Design%20Site%20Icons/chevron_up.svg";
    },
    downIconSrc() {
      return "https://res.cloudinary.com/avd/image/upload/v1633721088/Icons/New%20Design%20Site%20Icons/chevron_down.svg";
    },
    errorIconSrc() {
      return "https://res.cloudinary.com/avd/image/upload/v1661964419/Icons/New%20Design%20Site%20Icons/error.svg";
    },
    showUpIcon() {
      if (this.manufacturerSelectedByUser) {
        return false;
      }
      return this.showManufacturerMenu && !this.isLoading;
    },
    showDownIcon() {
      if (this.manufacturerSelectedByUser) {
        return false;
      }
      return !this.showManufacturerMenu && !this.isLoading;
    },
    showClearIcon() {
      return !this.isLoading && this.manufacturerSelectedByUser;
    },
    showClearManufacturerTextIconX() {
      return this.manufacturerFilterText.length > 0;
    },
    showClearModelTextIconX() {
      return this.modelFilterText.length > 0;
    },
    styleVariable() {
      return javascriptStyleVariables;
    },
    selectedManufacturerDataAttribute() {
      let selectedManufacturerMsg;
      if (this.selectedManufacturer) {
        selectedManufacturerMsg = this.selectedManufacturer;
      } else {
        selectedManufacturerMsg = "";
      }

      return {
        [this
          .dataAttributeSelectedManufacturer]: selectedManufacturerMsg.toLowerCase()
      };
    },
    selectedModelDataAttribute() {
      return {
        "data-selected-model": this.modelSelectedAnalytics
      };
    },
    selectedManufacturerSearchTermDataAttribute() {
      return {
        "data-manufacturer-search-term": this.selectedManufacturerSearchTerm
      };
    },
    selectedModelSearchTermDataAttribute() {
      return {
        "data-selected-model-search-term": this.modelSearchTerm
      };
    },
    combinedDataAttributes() {
      return {
        ...this.selectedManufacturerDataAttribute,
        ...this.selectedManufacturerSearchTermDataAttribute,
        ...this.selectedModelDataAttribute,
        ...this.selectedModelSearchTermDataAttribute
      };
    },
    selectedManufacturerWithAnalyticsClass() {
      const baseClass = "dropbtn";
      if (
        this.analyticsClassSelectedManufacturer &&
        this.manufacturerSelectedByUser
      ) {
        return `${baseClass} ${this.analyticsClassSelectedManufacturer}`;
      } else {
        return `${baseClass} ${this.analyticsClassManufacturer}`;
      }
    }
  },
  watch: {
    $refs: {
      handler(newRefs, oldRefs) {
        if (
          newRefs.getBoundingClientRect() !== oldRefs.getBoundingClientRect()
        ) {
          this.sendComponentRefs();
        }
      },
      deep: true
    }
  },
  components: {
    LoadingSpinner,
    XSvg
  }
};
</script>

<style lang="scss" scoped>
/* Show the dropdown menu (use JS to add this class to the .dropdown-content container when the user clicks on the dropdown button) */
.dropdown {
  margin-bottom: 64px;
  &.show {
    margin-bottom: 16px;
    display: block;
  }
}

.dropbtn {
  height: 48px;
  cursor: pointer;
  min-width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid $lightgray;
  background-color: unset;
  &:focus {
    @include focus_outline();
  }
  &__msg {
    color: $lightgray;
    @include hdg();
    @include body_small();
    margin-left: 8px;

    @media (min-width: $media-min-width-desktop) {
      @include body_standard();
      margin-left: 8px;
    }

    &-userSelected {
      @include hdg();
      @include body_small();
      margin-left: 8px;

      @media (min-width: $media-min-width-desktop) {
        @include body_standard();
        margin-left: 8px;
      }
    }
  }
  &__icon {
    padding-right: 8px;
  }
}

.search-input {
  width: calc(100% - 12px);
  height: 37px;
  margin-top: 6px !important;
  margin-left: 6px !important;
  margin-bottom: 6px !important;
  padding-left: 6px;
  border: 1px solid $black-25;
  border-radius: 0px;
  color: $black85;
  margin: 6px 6px;
  @include hdg();
  // for .search-input font size needs additional important
  @include body_small();
  font-size: 16px !important;

  @media (min-width: $media-min-width-desktop) {
    @include body_standard();
    font-size: 20px !important;
  }

  &:hover,
  :focus {
    outline: none;
  }
}
.dropdown-content {
  display: none;
  min-width: 230px;
  z-index: 1;
  min-width: 100%;
  border: 1px solid $lightgray;
  margin-bottom: 64px;

  input::placeholder {
    color: $black85;
    @include hdg();
    @include body_small();
    @media (min-width: $media-min-width-desktop) {
      @include body_standard();
    }
  }
}

.dropdown-scroll {
  max-height: 269px;
  overflow-y: auto;
}

.dropdown-content .dropDown-option {
  display: block;
  min-width: 100%;
  background-color: unset;
  padding-bottom: 8px;
  padding-top: 8px;
  padding-left: 20px;
  padding-right: 20px;
}

#manufacturerDropdown .dropDown-option:hover,
#manufacturerDropdown .dropDown-option:focus {
  background-color: $neutral100;
}

#modelDropdown .dropDown-option:hover,
#modelDropdown .dropDown-option:focus-visible {
  background-color: $success-100;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

#modelDropdown .dropDown-option:hover::after,
#modelDropdown .dropDown-option:focus-visible::after {
  content: url("https://res.cloudinary.com/legrandav/image/upload/v1707245898/checkmark_circle_success.svg");
  height: 24px;
  display: inline-block;
  align-items: center;
  padding-top: 2px;
}

.option-text {
  @include hdg();
  @include body_standard();
}

.option-required-product {
  @include hdg();
  @include caption();
  display: flex;
  align-items: center;
  background-color: $warning-200;
  border: 1px solid $warning-600;
  height: 37px;
  width: 249px;
  padding-left: 6px;
}

.required-product-link {
  text-decoration: underline;
  color: $primary;
  &:hover,
  &:focus {
    color: $primary-300;
  }
}

.errorIcon {
  margin-right: 6px;
}

.toggleDropdownBtn {
  margin-right: 12px;
  &:focus {
    @include focus_outline();
  }
}

.clearbtnAndUpDownIndicatorWrapper {
  display: flex;
  align-items: center;
}

.clearbtn {
  margin-right: -2px;
  margin-bottom: -9px;
  margin-top: 1px;

  &:focus {
    @include focus_outline();
  }

  .vue-close-icon {
    margin-top: 4px;
  }
}

#manufacturerDropdown {
  box-shadow: 0 8px 16px 0 rgb(0 0 0 / 20%);
}

#modelDropdown {
  margin-top: 16px;
}

#manufacturerDropdown,
#modelDropdown {
  input:focus {
    @include focus_outline();
  }
}

.skeletonBox.loadingDropdown {
  margin-top: 16px;
  height: 288px;
}

.inputWithBtnWrapper {
  position: relative;
}

.buttonInsideInput {
  position: absolute;
  right: 6px;
  top: 5px;
  cursor: pointer;
}
.svgWrapper {
  padding: 7.5px 20px;
}
</style>
